import { useEffect, useRef } from 'react';
import { useWatch } from 'react-hook-form';
import type { Control, UseFormReset } from 'react-hook-form';
import type { NavigationType } from 'react-router-dom';

const DEBOUNCE_MS = 1000;
const RESTORE_WINDOW_MS = 15 * 60 * 1000;

type DraftRestoreMode = 'any' | 'pop-only';

interface DraftRestoreRequest {
  mode: DraftRestoreMode;
  timestamp: number;
}

export function buildDraftScopeKey(siteType: string, clientName: string, programName: string) {
  return `${siteType}:${clientName}:${programName}`;
}

function draftKey(scopeKey: string) {
  return `microsites-form-draft-${scopeKey}`;
}

function draftMetaKey(scopeKey: string) {
  return `microsites-form-draft-meta-${scopeKey}`;
}

function draftRestoreKey(scopeKey: string) {
  return `microsites-form-draft-restore-${scopeKey}`;
}

export function requestDraftRestore(scopeKey: string, mode: DraftRestoreMode = 'any') {
  const request: DraftRestoreRequest = {
    mode,
    timestamp: Date.now(),
  };

  sessionStorage.setItem(draftRestoreKey(scopeKey), JSON.stringify(request));
}

function consumeDraftRestoreRequest(scopeKey: string, navigationType?: NavigationType): boolean {
  const key = draftRestoreKey(scopeKey);
  const raw = sessionStorage.getItem(key);
  if (!raw) {
    return false;
  }

  let request: DraftRestoreRequest;

  try {
    request = raw === '1'
      ? { mode: 'any', timestamp: Date.now() }
      : JSON.parse(raw) as DraftRestoreRequest;
  } catch {
    sessionStorage.removeItem(key);
    return false;
  }

  const isRecent = typeof request.timestamp === 'number' && Date.now() - request.timestamp <= RESTORE_WINDOW_MS;
  if (!isRecent) {
    sessionStorage.removeItem(key);
    return false;
  }

  if (request.mode === 'pop-only' && navigationType !== 'POP') {
    return false;
  }

  sessionStorage.removeItem(key);
  return true;
}

function getNavigationType(): string | undefined {
  const [entry] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
  return entry?.type;
}

function shouldRestoreDraft(): boolean {
  return getNavigationType() === 'back_forward';
}

function isBrowserBackForwardNavigation(navigationType?: NavigationType): boolean {
  if (navigationType && navigationType !== 'POP') {
    return false;
  }

  return shouldRestoreDraft();
}

interface UseFormDraftOptions {
  control: Control;
  reset: UseFormReset<Record<string, unknown>>;
  scopeKey: string;
  navigationType?: NavigationType;
  allowRestore?: boolean;
  ignoredFieldIds?: string[];
}

export function useFormDraft({
  control,
  reset,
  scopeKey,
  navigationType,
  allowRestore = false,
  ignoredFieldIds = [],
}: UseFormDraftOptions) {
  const dataKey = draftKey(scopeKey);
  const metaKey = draftMetaKey(scopeKey);
  const values = useWatch({ control });
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialLoadCompleteRef = useRef(false);

  const clearDraft = () => {
    sessionStorage.removeItem(dataKey);
    sessionStorage.removeItem(metaKey);
  };

  const saveDraftSnapshot = (snapshot: Record<string, unknown>) => {
    const sanitizedSnapshot = Object.fromEntries(
      Object.entries(snapshot).filter(([key]) => !ignoredFieldIds.includes(key)),
    );
    sessionStorage.setItem(dataKey, JSON.stringify(sanitizedSnapshot));
    sessionStorage.setItem(metaKey, JSON.stringify({ timestamp: Date.now() }));
  };

  // Restore a recent draft only when the page load was triggered by browser Back/Forward.
  useEffect(() => {
    const metaRaw = sessionStorage.getItem(metaKey);
    const dataRaw = sessionStorage.getItem(dataKey);

    const restoreFromBrowserHistory = isBrowserBackForwardNavigation(navigationType);
    const restoreFromAppNavigation = consumeDraftRestoreRequest(scopeKey, navigationType);

    if ((allowRestore || restoreFromBrowserHistory || restoreFromAppNavigation) && metaRaw && dataRaw) {
      try {
        const meta = JSON.parse(metaRaw) as { timestamp?: number };
        const isRecent = typeof meta.timestamp === 'number' && Date.now() - meta.timestamp <= RESTORE_WINDOW_MS;

        if (isRecent) {
          reset(JSON.parse(dataRaw) as Record<string, unknown>);
          initialLoadCompleteRef.current = true;
          return;
        }
      } catch {
        clearDraft();
      }
    }

    clearDraft();
    reset();
    initialLoadCompleteRef.current = true;
  // reset is stable across renders; this effect should only run for a fresh mount/route load,
  // not when wizard navigation pushes a new history entry on the same page.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowRestore, scopeKey, dataKey, metaKey]);

  // Debounced auto-save whenever form values change.
  useEffect(() => {
    if (!initialLoadCompleteRef.current) return;

    if (debounceRef.current !== null) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      const hasData = Object.entries(values as Record<string, unknown>).some(([key, v]) => {
        if (ignoredFieldIds.includes(key)) return false;
        if (Array.isArray(v)) return v.length > 0;
        if (typeof v === 'string') return v.trim() !== '';
        return v !== undefined && v !== null && v !== false;
      });

      if (hasData) {
        saveDraftSnapshot(values as Record<string, unknown>);
      } else {
        clearDraft();
      }
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current !== null) clearTimeout(debounceRef.current);
    };
  }, [values, dataKey, metaKey, ignoredFieldIds]);

  return { clearDraft, saveDraftSnapshot };
}
